import React, { useMemo, useState, useCallback } from 'react'
import { Row, Col, Nav, Alert } from 'react-bootstrap'
import { FaExclamationTriangle, FaExchangeAlt } from 'react-icons/fa'
import { Project } from '../../../types/models/project'
import {
  Model,
  TrainingJobEntryBBox,
  TrainingJobEntryClassification,
  TrainingStatsBBox,
  TrainingStatsClassification,
} from '../../../types/models/model'
import { RunInferenceResponse } from '../../../types/prediction'
import { useRunRealtimeInferenceMutation } from '../../../services/modelsApi'
import { MetricProgressGrid } from './components'
import ComparisonInferenceView from './components/ComparisonInferenceView'
import DatasetComparisonView from './components/DatasetComparisonView'
import FileUploader from '../../FileUploader'
import * as S from './styles'
import { ChartOptions } from '../../cards/MetricProgressCard'
import CompactModelCard from './components/CompactModelCard'

export type ModelComparisonCoreProps = {
  project?: Project
  model1?: Model
  model2?: Model
  initialTab?: 'performance' | 'realtime' | 'dataset'
  onRequestSelectModel2?: () => void
  onError?: (message: string) => void
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (
    error &&
    typeof error === 'object' &&
    'data' in error &&
    typeof (error as { data?: { message?: string } }).data === 'object' &&
    (error as { data?: { message?: string } }).data?.message
  ) {
    return (error as { data: { message: string } }).data.message
  }
  return fallback
}

const ModelComparisonCore: React.FC<ModelComparisonCoreProps> = ({
  project,
  model1,
  model2,
  initialTab = 'performance',
  onRequestSelectModel2,
  onError,
}) => {
  const [activeTab, setActiveTab] = useState<'performance' | 'realtime' | 'dataset'>(initialTab)
  const [dropFiles, setDropFiles] = useState<File[] | null>(null)
  const [inferenceResults1, setInferenceResults1] = useState<RunInferenceResponse | null>(null)
  const [inferenceResults2, setInferenceResults2] = useState<RunInferenceResponse | null>(null)
  const [showConfidence, setShowConfidence] = useState(true)

  const [runInference] = useRunRealtimeInferenceMutation()

  const gradients = useMemo(
    () => ({
      metrics: 'linear-gradient(45deg, #402563, #2042ba)',
    }),
    []
  )

  const modelColors = useMemo(
    () => ({
      model1: {
        border: 'rgba(99, 102, 241, 1)',
        background: 'rgba(99, 102, 241, 0.2)',
      },
      model2: {
        border: 'rgba(52, 211, 153, 1)',
        background: 'rgba(52, 211, 153, 0.2)',
      },
    }),
    []
  )

  const chartOptions: ChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 10, right: 15, bottom: 10, left: 15 } },
      scales: {
        y: {
          type: 'linear' as const,
          beginAtZero: true,
          max: 100,
          grid: { color: 'rgba(255, 255, 255, 0.1)' },
          ticks: { color: '#ffffff', callback: (value: number | string) => `${value}%` },
        },
        x: {
          type: 'category' as const,
          grid: { display: false },
          ticks: { color: '#ffffff' },
        },
      },
      plugins: {
        legend: {
          labels: { color: '#ffffff', padding: 10, font: { size: 12 } },
          align: 'end' as const,
        },
      },
    }),
    []
  )

  const getMetricProgressData = useCallback(
    (metricKey: string) => {
      const getEntries = (model?: Model) => {
        if (!model?.training_stats) return []

        const trainingStats =
          project?.annotation_type === 'bounding_box' ||
          project?.annotation_type === 'oriented_bounding_box'
            ? (model.training_stats.bounding_box as TrainingStatsBBox)
            : (model.training_stats.classification as TrainingStatsClassification)

        if (!trainingStats?.training_job_metrics) return []

        const metricsMap = trainingStats.training_job_metrics as Record<
          string,
          { training_job_entries: (TrainingJobEntryBBox | TrainingJobEntryClassification)[] }
        >

        const preferredKey =
          trainingStats.training_job_name && metricsMap[trainingStats.training_job_name]
            ? trainingStats.training_job_name
            : undefined

        const fallbackKey = !preferredKey
          ? Object.keys(metricsMap).sort(
              (a, b) =>
                (metricsMap[b]?.training_job_entries?.length || 0) -
                (metricsMap[a]?.training_job_entries?.length || 0)
            )[0]
          : undefined

        const resolvedKey = preferredKey || fallbackKey
        if (!resolvedKey) return []

        const jobEntries = metricsMap[resolvedKey]?.training_job_entries || []
        return jobEntries as (TrainingJobEntryBBox | TrainingJobEntryClassification)[]
      }

      const model1Entries = getEntries(model1)
      const model2Entries = getEntries(model2)

      const getMetricValue = (entry: TrainingJobEntryBBox | TrainingJobEntryClassification) => {
        if (
          project?.annotation_type === 'bounding_box' ||
          project?.annotation_type === 'oriented_bounding_box'
        ) {
          const bboxEntry = entry as TrainingJobEntryBBox
          switch (metricKey) {
            case 'mAP':
              return bboxEntry.metrics_map_50_95 ? bboxEntry.metrics_map_50_95 * 100 : 0
            case 'precision':
              return bboxEntry.metrics_precision ? bboxEntry.metrics_precision * 100 : 0
            case 'recall':
              return bboxEntry.metrics_recall ? bboxEntry.metrics_recall * 100 : 0
            case 'f1':
              if (bboxEntry.metrics_precision && bboxEntry.metrics_recall) {
                return (
                  ((2 * bboxEntry.metrics_precision * bboxEntry.metrics_recall) /
                    (bboxEntry.metrics_precision + bboxEntry.metrics_recall) || 0) * 100
                )
              }
              return 0
            default:
              return 0
          }
        } else {
          const classEntry = entry as TrainingJobEntryClassification
          switch (metricKey) {
            case 'accuracy':
              return classEntry.accuracy ? classEntry.accuracy * 100 : 0
            default:
              return 0
          }
        }
      }

      return {
        labels: Array.from(
          { length: Math.max(model1Entries.length, model2Entries.length) },
          (_, i) => `Epoch ${i + 1}`
        ),
        datasets: [
          {
            label: model1?.name || 'Model 1',
            data: model1Entries.map((entry) => getMetricValue(entry)),
            borderColor: 'rgba(13, 202, 240, 1)',
            backgroundColor: 'rgba(13, 202, 240, 0.2)',
            tension: 0.4,
          },
          {
            label: model2?.name || 'Model 2',
            data: model2Entries.map((entry) => getMetricValue(entry)),
            borderColor: 'rgba(25, 135, 84, 1)',
            backgroundColor: 'rgba(25, 135, 84, 0.2)',
            tension: 0.4,
          },
        ],
      }
    },
    [project?.annotation_type, model1, model2]
  )

  const handleInference = async (acceptedFiles: File[]) => {
    if (!model1 || !model2) return

    setDropFiles(acceptedFiles)
    setInferenceResults1(null)
    setInferenceResults2(null)

    const formData1 = new FormData()
    const formData2 = new FormData()
    acceptedFiles.forEach((df) => {
      formData1.append('files', df, df.name)
      formData2.append('files', df, df.name)
    })

    try {
      const [result1, result2] = await Promise.all([
        runInference({ id: model1.id, data: formData1, heatmap: false, threshold: 0.1 }).unwrap(),
        runInference({ id: model2.id, data: formData2, heatmap: false, threshold: 0.1 }).unwrap(),
      ])

      const sortedResult1 = [...(result1 || [])].sort((a, b) =>
        a.filename.localeCompare(b.filename)
      )
      const sortedResult2 = [...(result2 || [])].sort((a, b) =>
        a.filename.localeCompare(b.filename)
      )
      setInferenceResults1(sortedResult1 || null)
      setInferenceResults2(sortedResult2 || null)
    } catch (error: unknown) {
      const msg = getErrorMessage(error, 'Error running inference')
      if (onError) onError(msg)
    }
  }

  const handleResetInference = () => {
    setInferenceResults1(null)
    setInferenceResults2(null)
    setDropFiles(null)
  }

  const isDeployed = (model?: Model) => model?.deployment?.status?.toLowerCase?.() === 'in_service'

  const modelCards = (
    <div
      className="mb-3 flex-shrink-0 d-flex justify-content-center"
      style={{ position: 'relative', zIndex: 1 }}
    >
      <div
        className="d-grid align-items-center"
        style={{ gridTemplateColumns: '340px auto 340px', gap: '.75rem' }}
      >
        <div>
          <div
            style={{
              fontSize: '.7rem',
              color: '#6366f1',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '.5px',
              marginBottom: '.25rem',
            }}
          >
            Base Model
          </div>
          {model1 && <CompactModelCard model={model1} project={project} borderColor="#6366f1" />}
        </div>
        <div
          className="d-flex flex-column align-items-center"
          style={{ gap: '.25rem', padding: '0 .5rem' }}
        >
          <FaExchangeAlt style={{ color: '#9333ea', fontSize: '1.25rem' }} />
          <div
            style={{
              color: '#ffffff',
              fontSize: '.7rem',
              fontWeight: 600,
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
            }}
          >
            VS
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: '.7rem',
              color: '#34d399',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '.5px',
              marginBottom: '.25rem',
            }}
          >
            Fine-Tuned Iteration
          </div>
          {/* The actual selector button is owned by the container; we call back */}
          {model2 ? (
            <CompactModelCard
              model={model2}
              project={project}
              borderColor="#34d399"
              showSelector={true}
              onShowSelector={onRequestSelectModel2}
              isIteration={true}
            />
          ) : (
            <button
              className="btn btn-outline-success btn-sm"
              onClick={onRequestSelectModel2}
              type="button"
            >
              Select comparison
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      <div className="d-flex flex-row w-100">
        {/* Top Tabs */}
        <div className="d-flex flex-column overflow-hidden h-100 ps-4" style={{ minHeight: 0 }}>
          {model2 && (
            <div className="border border-secondary rounded p-2 me-2">
              <div className="text-white fw-semibold small mb-2">Active View</div>
              <Nav
                variant="pills"
                activeKey={activeTab}
                onSelect={(k) => {
                  if (k) setActiveTab(k as 'performance' | 'realtime' | 'dataset')
                }}
                className="flex-column gap-1"
              >
                <Nav.Item>
                  <Nav.Link
                    eventKey="performance"
                    className={
                      (activeTab === 'performance' ? 'text-white' : 'text-white-50') + ' py-1'
                    }
                  >
                    Performance
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="dataset"
                    className={(activeTab === 'dataset' ? 'text-white' : 'text-white-50') + ' py-1'}
                  >
                    Dataset
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="realtime"
                    className={
                      (activeTab === 'realtime' ? 'text-white' : 'text-white-50') + ' py-1'
                    }
                  >
                    Real-Time
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </div>
          )}
        </div>
        <div className="d-flex flex-column flex-grow-1 h-100" style={{ minHeight: 0 }}>
          {model2 && <>{modelCards}</>}
        </div>
      </div>

      <div
        className="d-flex flex-row flex-grow-1 overflow-hidden w-100 h-100"
        style={{ minHeight: 0 }}
      >
        {/* Top Tabs */}
        <div
          className="d-flex flex-column overflow-hidden w-100 h-100"
          style={{ minHeight: 0, position: 'relative', zIndex: 0 }}
        >
          <S.MetricsSection
            className="flex-grow-1 overflow-auto h-100"
            style={{ minHeight: 0, zIndex: 0 }}
          >
            {model2 ? (
              <>
                {activeTab === 'performance' && (
                  <MetricProgressGrid
                    projectAnnotationType={project?.annotation_type}
                    gradient={gradients.metrics}
                    getMetricProgressData={getMetricProgressData}
                    chartOptions={chartOptions}
                    model1Color={modelColors.model1}
                    model2Color={modelColors.model2}
                  />
                )}

                {activeTab === 'dataset' && (
                  <Row className="h-100" style={{ minHeight: 0 }}>
                    <Col md={12} className="h-100" style={{ minHeight: 0 }}>
                      <DatasetComparisonView model1={model1} model2={model2} />
                    </Col>
                  </Row>
                )}

                {activeTab === 'realtime' && (
                  <Row>
                    <Col md={12}>
                      {!isDeployed(model1) || !isDeployed(model2) ? (
                        <Alert variant="warning">
                          <FaExclamationTriangle className="me-2" /> Both models must be deployed to
                          perform real-time inference comparison.
                        </Alert>
                      ) : (
                        <div className="scrollable-content">
                          {inferenceResults1 && inferenceResults2 ? (
                            <ComparisonInferenceView
                              results1={inferenceResults1}
                              results2={inferenceResults2}
                              dropFiles={dropFiles}
                              model1={model1}
                              model2={model2}
                              onReset={handleResetInference}
                              showConfidence={showConfidence}
                              onToggleConfidence={() => setShowConfidence((v) => !v)}
                            />
                          ) : (
                            <div className="upload-container">
                              <div className="upload-wrapper">
                                <div className="upload-content">
                                  <FileUploader
                                    onFileSelect={handleInference}
                                    uploading={false}
                                    numFiles={dropFiles?.length || 0}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </Col>
                  </Row>
                )}
              </>
            ) : (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ minHeight: '400px' }}
              >
                <div className="text-center" style={{ maxWidth: '600px' }}>
                  <div className="mb-4">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: '80px',
                        height: '80px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: '2px solid #404040',
                      }}
                    >
                      <FaExchangeAlt size={32} className="text-white" />
                    </div>
                    <h4 className="text-white mb-3">Model Comparison</h4>
                    <p className="text-light mb-4" style={{ color: '#e9ecef', fontSize: '1.1rem' }}>
                      Compare model performance, training metrics, and run side-by-side inference
                      tests
                    </p>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-4">
                      <div
                        className="p-3"
                        style={{
                          background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                          borderRadius: '12px',
                          border: '1px solid rgba(102, 126, 234, 0.2)',
                        }}
                      >
                        <div className="text-primary mb-2">
                          <i className="fas fa-chart-line" style={{ fontSize: '1.5rem' }}></i>
                        </div>
                        <h6 className="text-white mb-2">Performance Metrics</h6>
                        <small className="text-light" style={{ color: '#e9ecef' }}>
                          Compare accuracy, precision, recall, and other training metrics
                        </small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div
                        className="p-3"
                        style={{
                          background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                          borderRadius: '12px',
                          border: '1px solid rgba(102, 126, 234, 0.2)',
                        }}
                      >
                        <div className="text-primary mb-2">
                          <i className="fas fa-brain" style={{ fontSize: '1.5rem' }}></i>
                        </div>
                        <h6 className="text-white mb-2">Real-time Inference</h6>
                        <small className="text-light" style={{ color: '#e9ecef' }}>
                          Test both models with the same input and compare predictions
                        </small>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div
                        className="p-3"
                        style={{
                          background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                          borderRadius: '12px',
                          border: '1px solid rgba(102, 126, 234, 0.2)',
                        }}
                      >
                        <div className="text-primary mb-2">
                          <i className="fas fa-database" style={{ fontSize: '1.5rem' }}></i>
                        </div>
                        <h6 className="text-white mb-2">Dataset Analysis</h6>
                        <small className="text-light" style={{ color: '#e9ecef' }}>
                          Compare how models perform on different data subsets
                        </small>
                      </div>
                    </div>
                  </div>

                  <div
                    className="p-4"
                    style={{
                      background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
                      borderRadius: '16px',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                    }}
                  >
                    <h6 className="text-white mb-3">Ready to compare models?</h6>
                    <p className="text-light mb-4" style={{ color: '#e9ecef' }}>
                      Select a model to compare with{' '}
                      <strong>{model1?.name || 'the current model'}</strong> and start analyzing
                      their differences.
                    </p>
                    {onRequestSelectModel2 && (
                      <button
                        type="button"
                        className="btn btn-primary btn-lg px-4"
                        onClick={onRequestSelectModel2}
                      >
                        <FaExchangeAlt className="me-2" />
                        Select Comparison Model
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </S.MetricsSection>
        </div>
      </div>
    </>
  )
}

export default ModelComparisonCore
